import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Stack,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { PdfPreview } from '@/components/PdfPreview';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/AuthProvider';

interface ImageUploadProps {
  value: string[];
  onChange: (paths: string[]) => void;
  maxFiles?: number;
}

export function ImageUpload({ value, onChange, maxFiles = 5 }: ImageUploadProps) {
  const { user } = useAuth();
  const [uploading, setUploading] = useState(false);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!user) return;

      setUploading(true);
      const newPaths: string[] = [];

      for (const file of acceptedFiles) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

        const { error } = await supabase.storage
          .from('assignment-images')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false,
          });

        if (!error) {
          newPaths.push(fileName);
        } else {
          console.error('Upload error:', error);
        }
      }

      onChange([...value, ...newPaths]);
      setUploading(false);
    },
    [user, value, onChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp', '.heic'],
      'application/pdf': ['.pdf'],
    },
    maxFiles: maxFiles - value.length,
    disabled: uploading || value.length >= maxFiles,
  });

  const handleRemove = async (path: string) => {
    await supabase.storage.from('assignment-images').remove([path]);
    onChange(value.filter((p) => p !== path));
  };

  const getImageUrl = (path: string) => {
    const { data } = supabase.storage.from('assignment-images').getPublicUrl(path);
    return data.publicUrl;
  };

  return (
    <Box>
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: value.length >= maxFiles ? 'not-allowed' : 'pointer',
          bgcolor: isDragActive ? 'action.hover' : 'background.paper',
          transition: 'all 0.2s',
          '&:hover': {
            borderColor: value.length >= maxFiles ? 'divider' : 'primary.main',
          },
        }}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <CircularProgress />
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" gutterBottom>
              {isDragActive ? '放開以上傳檔案' : '拖放試卷圖片或 PDF 到此處'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              或點擊選擇檔案（最多 {maxFiles} 個，支援圖片及 PDF）
            </Typography>
          </>
        )}
      </Box>

      {value.length > 0 && (
        <Stack spacing={2} sx={{ mt: 2 }}>
          {value.map((path, index) => {
            const isPdf = path.toLowerCase().endsWith('.pdf');
            const url = getImageUrl(path);

            if (isPdf) {
              return (
                <PdfPreview
                  key={path}
                  url={url}
                  actions={
                    <Button size="small" color="error" startIcon={<DeleteIcon fontSize="small" />} onClick={() => handleRemove(path)}>
                      移除
                    </Button>
                  }
                />
              );
            }

            return (
              <Box key={path} sx={{ position: 'relative', border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', display: 'inline-block' }}>
                <img
                  src={url}
                  alt={`圖片 ${index + 1}`}
                  loading="lazy"
                  style={{ maxWidth: '100%', maxHeight: 400, objectFit: 'contain', display: 'block' }}
                />
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <IconButton size="small" onClick={() => handleRemove(path)} sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
